import React from "react";
import LegalShell from "@/components/LegalShell";
import SEO from "@/components/SEO";

export default function PrivacyPage() {
  return (
    <>
      <SEO
        title="Privacy Policy | Postmoro"
        description="Read how Postmoro collects, uses, and protects the data behind your AI social media automation across Facebook, Instagram, LinkedIn, and X."
        path="/privacy"
      />
      <LegalShell
      title="Privacy Policy"
      subtitle="How Postmoro collects, uses, and protects the information that powers your AI marketing employee."
      lastUpdated="August 2026"
      testidPrefix="privacy"
    >
      <p>
        Postmoro is an AI-powered business growth platform that brings together content
        creation, publishing, automation, brand intelligence, marketing ideas, and social
        media management. This Privacy Policy explains how <strong>Postmoro LLC</strong>
        (“Postmoro,” “we,” “us,” or “our”) collects, uses, stores, and protects information
        when you use Postmoro, and the rights you have to control your data. By using
        Postmoro you agree to the practices described here.
      </p>

      <h2>1. Information We Collect</h2>

      <h3>Account &amp; profile information</h3>
      <ul>
        <li>Your name, email address, and authentication tokens used to sign in to Postmoro.</li>
        <li>Profile settings such as display name, timezone, and preferences.</li>
      </ul>

      <h3>Business profile information</h3>
      <ul>
        <li>Business name, business type/industry, brand voice, location, audience description, and goals.</li>
        <li>Optional images, color palette, and other branding details you choose to share.</li>
      </ul>

      <h3>Generated content</h3>
      <ul>
        <li>Captions, hashtags, image prompts, and AI-generated images created with our service.</li>
        <li>Edits you make to AI drafts, approval status, scheduling times, and publishing history.</li>
      </ul>

      <h3>Connected social account tokens</h3>
      <ul>
        <li>
          OAuth access tokens, refresh tokens, and account identifiers for the social networks you
          connect (e.g. Facebook Pages, Instagram Business, LinkedIn, X / Twitter). These are stored
          securely and used <strong>only</strong> to publish on your behalf at your direction.
        </li>
        <li>Page IDs, page names, Instagram Business account IDs, LinkedIn member/organization URNs, and similar metadata required to publish.</li>
      </ul>

      <h3>Billing data</h3>
      <ul>
        <li>
          Subscription plan, status, billing period, and subscription identifiers used to enforce
          plan limits.
        </li>
        <li>
          <strong>Payment details are handled entirely by Stripe.</strong> Postmoro never sees or stores
          your full card number; only Stripe customer/subscription IDs are stored on our side.
        </li>
      </ul>

      <h3>Usage analytics</h3>
      <ul>
        <li>Counts and timestamps of generated posts, scheduled posts, and successful publishes.</li>
        <li>Aggregate platform-level usage to compute monthly plan limits and feature availability.</li>
        <li>Diagnostic logs (request IDs, error codes) used to maintain service reliability.</li>
      </ul>

      <h3>Business contact and marketing details</h3>
      <ul>
        <li>
          Optional business contact details such as phone number, website URL, booking link,
          business email, address, hours of operation, services, products, preferred
          calls-to-action, and other details you choose to provide to personalize
          generated content.
        </li>
      </ul>

      <h3>Brand Learning / past post examples</h3>
      <ul>
        <li>
          Past post examples, captions, marketing notes, performance notes, tags, and brand
          style examples you choose to provide so Postmoro can learn your business voice and
          generate more personalized content.
        </li>
      </ul>

      <h3>AI generation usage</h3>
      <ul>
        <li>
          Successful AI generation events, including caption generation, hashtag generation,
          image generation, brand style analysis, marketing ideas, regenerations, and Autopilot
          generation, used to enforce plan limits and maintain reliability.
        </li>
      </ul>

      <h3>Support information</h3>
      <ul>
        <li>
          Support ticket information such as subject, category, priority, message, optional
          screenshot/image URL, page URL, browser information, timestamp, account plan, and
          related post/platform details.
        </li>
      </ul>

      <h3>Media Library</h3>
      <ul>
        <li>
          Images you upload to your Media Library for reuse across drafts and scheduled posts,
          along with basic metadata (filename, upload date, size, and where you referenced the
          image inside Postmoro).
        </li>
      </ul>

      <h3>Competitor Analysis workspace (if enabled for your account)</h3>
      <ul>
        <li>
          Competitor details you manually enter, including competitor name, industry, location,
          website URL, social profile URLs, and free-text notes. Postmoro stores these as text
          metadata; in the current Phase 1 build we do <strong>not</strong> fetch or scrape
          competitor websites or social profiles.
        </li>
        <li>
          AI-generated competitor analyses, opportunity records, and saved reports produced from
          the information you supply plus your own business profile and brand context.
        </li>
      </ul>

      <h3>Email Marketing workspace (if enabled for your account)</h3>
      <ul>
        <li>
          Contact lists and contact information you enter or upload (including CSV imports),
          such as email address, name, and any custom fields you attach to a contact.
        </li>
        <li>
          Email campaign drafts, subject lines, HTML/text bodies, scheduling metadata, and
          Autopilot preferences. The current Phase 1 build is a foundation for future email
          delivery; it does <strong>not</strong> send bulk marketing email on your behalf.
        </li>
      </ul>

      <h3>Marketing and product-update emails (optional)</h3>
      <p>
        When you sign up, you may separately opt in to receive Postmoro product updates,
        new-feature announcements, tips, and occasional offers by email. You can also
        subscribe from our homepage without a Postmoro account. Participation is optional.
        We store your email address and marketing email preference for this purpose.
      </p>
      <p>
        You can turn marketing emails off at any time from <strong>Email Preferences</strong>
        in your account or by using the unsubscribe link in any Postmoro marketing email.
      </p>
      <p>
        Unsubscribing from marketing does <strong>not</strong> stop transactional emails such
        as receipts, password resets, security notices, account/service messages, or
        support-ticket replies.
      </p>

      <h2>2. How We Use Your Data</h2>
      <ul>
        <li>To generate captions, images, and ideas tailored to your business.</li>
        <li>To publish or schedule posts to the social platforms you&apos;ve explicitly connected.</li>
        <li>To enforce plan limits and provide accurate usage dashboards.</li>
        <li>To deliver customer support and respond to your requests.</li>
        <li>To improve product reliability and security through diagnostic logging.</li>
        <li>To send optional Postmoro product updates, tips, feature announcements, and marketing emails when you have opted in.</li>
      </ul>
      <p>
        We do <strong>not</strong> sell your data. We do not use your business content or your
        social account tokens for any purpose other than operating Postmoro on your behalf.
      </p>

      <h2>3. Disconnecting Social Accounts</h2>
      <p>
        You can disconnect any connected social account at any time from <strong>Settings
        → Connections</strong>. When you disconnect, Postmoro:
      </p>
      <ul>
        <li>Immediately stops publishing on that account.</li>
        <li>Deletes the stored OAuth tokens and account identifiers for that platform.</li>
        <li>Retains historical post records (captions and timestamps) unless you also delete them.</li>
      </ul>

      <h2>4. Requesting Data Deletion</h2>
      <p>
        You can request full account and data deletion at any time. See our{" "}
        <a href="/data-deletion">Data Deletion page</a> for the official request flow, or email{" "}
        <a href="mailto:support@postmoro.com">support@postmoro.com</a>. Deletion requests are
        processed within a reasonable timeframe as required by applicable law and platform policy.
      </p>

      <h2>5. Third-Party Services</h2>
      <p>
        Postmoro relies on a small number of trusted third parties:
      </p>
      <ul>
        <li><strong>Stripe</strong> — subscription billing and payments.</li>
        <li><strong>Meta (Facebook / Instagram), LinkedIn, X / Twitter</strong> — publishing through their official APIs.</li>
        <li><strong>OpenAI, Google, and similar LLM providers</strong> — AI text and image generation.</li>
        <li><strong>Google Analytics 4</strong> — aggregate site analytics on our public marketing pages. See the &ldquo;Google Analytics and cookies&rdquo; section below for exactly what we send and what we do not.</li>
      </ul>
      <p>
        Each provider has its own privacy policy that governs how it handles data we send it for
        you. We send only the minimum data needed to perform the requested action.
      </p>

      <h2>6. Google Analytics and Cookies</h2>
      <p>
        Postmoro uses Google Analytics 4 (measurement ID <code>G-ZBP9LW5KB6</code>) to understand
        how visitors find and use our marketing pages. Google Analytics is loaded only after you
        accept analytics cookies via the consent banner shown on your first visit. If you decline,
        no analytics cookies are set on your device and no analytics data is stored against a
        persistent identifier.
      </p>
      <p>
        When you accept analytics, we send Google Analytics:
      </p>
      <ul>
        <li>The page path you visited (for example, <code>/social-media-scheduler</code>).</li>
        <li>The page title.</li>
        <li>Standard browser information Google Analytics collects automatically (device type, approximate location derived from your anonymised IP, referrer domain).</li>
        <li>A random device identifier stored in a first-party cookie so Google can group your visits into one session.</li>
      </ul>
      <p>
        We <strong>never</strong> send Google Analytics your name, email address, post content,
        generated captions or images, connected-account handles, OAuth tokens, session
        identifiers, customer IDs, or billing information. Query strings and hash fragments are
        stripped from every URL before it reaches Google — this matters because our own
        authentication callback and password-reset pages sometimes carry short-lived tokens in
        the URL, and we make it structurally impossible for those tokens to be sent.
      </p>
      <p>
        We use <strong>Google Consent Mode v2</strong> in denied-by-default configuration. Ad
        storage, ad user data, and ad personalization signals are permanently denied — Postmoro
        does not run Google Ads and never shares this data with Google&apos;s advertising
        products. IP addresses are anonymised.
      </p>
      <p>
        You can change your analytics choice at any time by clearing your browser&apos;s site
        data for Postmoro; the consent banner will reappear on your next visit.
      </p>

      <h2>7. Security</h2>
      <p>
        We use industry-standard practices to protect your data, including encrypted credentials
        storage, scoped OAuth tokens, and access controls. No system is perfectly secure; you
        agree to notify us promptly if you suspect unauthorized access to your account.
      </p>

      <h2>8. Data Retention</h2>
      <p>
        We keep account, business profile, generated content, support tickets, and usage data
        for as long as your account is active or as needed to provide Postmoro. We may retain
        limited records after deletion when required for legal, billing, tax, fraud prevention,
        security, compliance, or dispute-resolution purposes.
      </p>

      <h2>9. Children</h2>
      <p>
        Postmoro is intended for business users and is not directed to children under 13.
        We do not knowingly collect personal information from children under 13.
      </p>

      <h2>10. No Sale of Personal Data</h2>
      <p>
        Postmoro does <strong>not</strong> sell your personal information, business profile
        information, uploaded images, generated content, connected social account tokens, or
        support ticket information. We use your data only to provide and improve the service,
        personalize AI-generated content, publish content at your direction, enforce plan limits,
        process billing through Stripe, maintain security, and respond to support requests.
      </p>

      <h2>11. Contact Us</h2>
      <p>
        Questions about this Privacy Policy? Email{" "}
        <a href="mailto:support@postmoro.com">support@postmoro.com</a>.
      </p>
    </LegalShell>
    </>
  );
}
